import { createFileRoute } from '@tanstack/react-router';
import { Badge, Card, Group, NumberFormatter, Select, SimpleGrid, Skeleton, Stack, Table, Text, Title } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import { IconChartBar } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { httpRequest } from '@/shared/api/client';
import { EmptyState } from '@/shared/components/EmptyState';

export const Route = createFileRoute('/_app/insights/monthly-invoices')({ component: MonthlyInvoicesPage });

interface SummaryRow {
  projectId: string | null;
  projectCode: string | null;
  projectName: string;
  customerName: string | null;
  isBillable: boolean;
  billingHours: number;
  ordinaryHours: number;
  overtimeHours: number;
  daysWorked: number;
  entriesCount: number;
}
interface Summary {
  year: number; month: number; from: string; to: string;
  totals: { billingHours: number; ordinaryHours: number; overtimeHours: number; projectsCount: number; billableProjects: number };
  rows: SummaryRow[];
}
interface TrendPoint { year: number; month: number; label: string; billingHours: number; }
interface Trend { from: string; monthsBack: number; series: TrendPoint[]; }

const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

function MonthlyInvoicesPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const summaryQ = useQuery({
    queryKey: ['monthly-summary', year, month],
    queryFn: () => httpRequest<Summary>(`/api/invoices/monthly-summary?year=${year}&month=${month}`, { endpointType: 'plain' }),
    staleTime: 5 * 60_000,
  });
  const trendQ = useQuery({
    queryKey: ['yearly-trend'],
    queryFn: () => httpRequest<Trend>('/api/invoices/yearly-trend?monthsBack=12', { endpointType: 'plain' }),
    staleTime: 10 * 60_000,
  });

  const chartData = (trendQ.data?.series ?? []).map((p) => ({
    month: p.label.slice(2),
    ore: p.billingHours,
  }));

  return (
    <Stack>
      <Title order={1}>Fatturato mensile</Title>
      <Group>
        <Select
          label="Anno"
          value={String(year)}
          onChange={(v) => setYear(Number(v ?? now.getFullYear()))}
          data={[year - 2, year - 1, year, year + 1].map((y) => ({ value: String(y), label: String(y) }))}
          w={120}
        />
        <Select
          label="Mese"
          value={String(month)}
          onChange={(v) => setMonth(Number(v ?? 1))}
          data={MONTH_LABELS.map((m, i) => ({ value: String(i + 1), label: m }))}
          w={120}
        />
      </Group>

      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Andamento ore fatturabili (12 mesi)</Title>
        {trendQ.isLoading ? (
          <Skeleton height={260} radius="md" />
        ) : chartData.length === 0 ? (
          <EmptyState
            icon={IconChartBar}
            title="Nessun dato di trend"
            description="Il grafico si popolerà appena verranno registrate ore fatturabili nei timesheet."
            py="md"
          />
        ) : (
          <BarChart
            h={260}
            data={chartData}
            dataKey="month"
            series={[{ name: 'ore', label: 'Ore fatturabili', color: 'blue.6' }]}
            tickLine="y"
            gridAxis="y"
            withLegend
          />
        )}
      </Card>

      {summaryQ.isLoading ? (
        <>
          <SimpleGrid cols={{ base: 2, md: 5 }}>
            {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} height={76} radius="md" />)}
          </SimpleGrid>
          <Skeleton height={240} radius="md" />
        </>
      ) : summaryQ.data && (
        <>
          <SimpleGrid cols={{ base: 2, md: 5 }}>
            <Card withBorder padding="md" radius="md">
              <Text size="xs" c="dimmed">Ore fatturabili</Text>
              <Text fw={700} size="xl"><NumberFormatter value={summaryQ.data.totals.billingHours} decimalScale={1} /></Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text size="xs" c="dimmed">Ore ordinarie</Text>
              <Text fw={700} size="xl"><NumberFormatter value={summaryQ.data.totals.ordinaryHours} decimalScale={1} /></Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text size="xs" c="dimmed">Straordinari</Text>
              <Text fw={700} size="xl"><NumberFormatter value={summaryQ.data.totals.overtimeHours} decimalScale={1} /></Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text size="xs" c="dimmed">Commesse coinvolte</Text>
              <Text fw={700} size="xl">{summaryQ.data.totals.projectsCount}</Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text size="xs" c="dimmed">Di cui fatturabili</Text>
              <Text fw={700} size="xl">{summaryQ.data.totals.billableProjects}</Text>
            </Card>
          </SimpleGrid>

          <Card withBorder padding={0} radius="md">
            <Title order={4} p="md">Dettaglio per commessa — {MONTH_LABELS[month - 1]} {year}</Title>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Codice</Table.Th>
                  <Table.Th>Commessa</Table.Th>
                  <Table.Th>Cliente</Table.Th>
                  <Table.Th>Stato</Table.Th>
                  <Table.Th ta="right">Ore fatt.</Table.Th>
                  <Table.Th ta="right">Ord.</Table.Th>
                  <Table.Th ta="right">Strao.</Table.Th>
                  <Table.Th ta="right">gg lavorati</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {summaryQ.data.rows.length === 0 && (
                  <Table.Tr><Table.Td colSpan={8}><Text c="dimmed" ta="center" py="md">Nessun timesheet nel periodo.</Text></Table.Td></Table.Tr>
                )}
                {summaryQ.data.rows.map((r) => (
                  <Table.Tr key={r.projectId ?? 'unassigned'}>
                    <Table.Td><code>{r.projectCode ?? '—'}</code></Table.Td>
                    <Table.Td>{r.projectName}</Table.Td>
                    <Table.Td>{r.customerName ?? '—'}</Table.Td>
                    <Table.Td>
                      {r.isBillable
                        ? <Badge color="green" variant="light">Fatturabile</Badge>
                        : <Badge color="gray" variant="light">Interna</Badge>}
                    </Table.Td>
                    <Table.Td ta="right"><Text fw={600}><NumberFormatter value={r.billingHours} decimalScale={1} /></Text></Table.Td>
                    <Table.Td ta="right"><NumberFormatter value={r.ordinaryHours} decimalScale={1} /></Table.Td>
                    <Table.Td ta="right"><NumberFormatter value={r.overtimeHours} decimalScale={1} /></Table.Td>
                    <Table.Td ta="right">{r.daysWorked}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </>
      )}
    </Stack>
  );
}
